/**
 * Cleans assistant message text before it is displayed or persisted.
 *
 * Romy's model output occasionally leaks two kinds of noise:
 *  1. Internal metadata tags (e.g. "[Artifact generated: …]", "[Reminder set: …]")
 *     that we inject into the conversation history for the model's own context
 *     (see Chat.tsx conversationHistoryForAPI). The model sometimes parrots them
 *     back into its visible reply.
 *  2. Raw HTML like "<br>", which react-markdown renders as literal text because
 *     rehype-raw is (intentionally) not enabled.
 *
 * Order matters: we convert <br> to a real newline FIRST. When a leaked tag runs
 * straight into real prose via "<br>" — e.g.
 *     "[Artifact generated: Questions for your OBGYN <br> Do you want to go over…"
 * the <br> becomes a clean line boundary, so the tag strip (which stops at a
 * newline) removes only the junk and the real follow-up question survives.
 *
 * We also strip citation markers like "[1]" / "[3, 4]" here so all assistant-text
 * cleanup lives in one place.
 */

// Known internal tag stems, matching the labels produced in Chat.tsx.
const INTERNAL_TAG_STEMS = [
  'Profile update proposed',
  'Artifact generated',
  'Artifact suggested',
  'Community topic suggested',
  'Clinical trials suggested for',
  'Goal plan created',
  'Meal plan created',
  'Reminder set',
  'Daily check-in',
  'Tracking protocol proposed',
  'Preference',
];

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Matches "[Stem …]" — with or without a closing bracket, terminating at the
// first "]" or newline (whichever comes first). The unterminated case is why we
// normalize <br> to a newline before running this.
const INTERNAL_TAG_RE = new RegExp(
  `\\[(?:${INTERNAL_TAG_STEMS.map(escapeRegExp).join('|')})[^\\]\\n]*\\]?`,
  'gi'
);

export function sanitizeAssistantText(text: string): string {
  if (!text) return text;
  return text
    // 1. HTML line breaks → real newlines (must run before tag stripping)
    .replace(/<br\s*\/?>/gi, '\n')
    // 2. Citation markers like [1], [3, 4] — meaningless without a reference list
    .replace(/\s*\[\d+(?:,\s*\d+)*\]/g, '')
    // 3. Leaked internal metadata tags (well-formed or unterminated)
    .replace(INTERNAL_TAG_RE, '')
    // 4. Tidy whitespace left behind by removals
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
}
